import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '../components/ui/Button'
import { Download, Upload, FolderOpen, ClipboardList, LogOut, LayoutDashboard, Ban } from 'lucide-react'
import { usePublicSettings } from '../hooks/usePublicSettings'

// ─── Auth helpers (same pattern as AuthContext) ──────────────────────────────
function getToken() {
  return sessionStorage.getItem('org_token') || sessionStorage.getItem('admin_token')
}
function getUser() {
  try {
    const raw = sessionStorage.getItem('org_user') || sessionStorage.getItem('admin_user')
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}
function saveSession(token, user) {
  sessionStorage.setItem('org_token', token)
  sessionStorage.setItem('org_user', JSON.stringify(user))
}
function clearSession() {
  sessionStorage.removeItem('org_token')
  sessionStorage.removeItem('org_user')
}

// ─── Login form ───────────────────────────────────────────────────────────────
function LoginForm({ onLogin }) {
  const [form, setForm]       = useState({ username: '', password: '' })
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const data = await api.post('/admin/login', form)
      if (!['organization', 'admin'].includes(data.user?.role)) {
        setError('هذا الحساب لا يملك صلاحية الاستيراد الجماعي')
        return
      }
      saveSession(data.token, data.user)
      onLogin(data.user)
    } catch (err) {
      setError(err.message || 'بيانات الدخول غير صحيحة')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4" dir="rtl">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-2xl font-bold text-stone-800 mb-2 text-center">تسجيل الدخول</h1>
        <p className="text-stone-500 text-sm text-center mb-6">الاستيراد الجماعي — للمنظمات والجهات</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">اسم المستخدم</label>
            <input
              type="text"
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              required
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">كلمة المرور</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              required
              autoComplete="current-password"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full">
            {loading ? 'جارٍ التحقق...' : 'دخول'}
          </Button>
        </form>
      </div>
    </div>
  )
}

// ─── Main Bulk Import Page ────────────────────────────────────────────────────
export default function BulkImportPage() {
  const navigate = useNavigate()
  const fileRef  = useRef()
  const { settings, loading: settingsLoading } = usePublicSettings()

  const [user, setUser]           = useState(() => getUser())
  const [file, setFile]           = useState(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult]       = useState(null)
  const [history, setHistory]     = useState([])
  const [histLoading, setHistLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('upload') // 'upload' | 'history'

  // Redirect non-org users who navigated here without org token
  useEffect(() => {
    if (!user) return
    if (!['organization', 'admin'].includes(user.role)) {
      clearSession()
      setUser(null)
    }
  }, [user])

  useEffect(() => {
    if (user && activeTab === 'history') loadHistory()
  }, [user, activeTab])

  async function loadHistory() {
    setHistLoading(true)
    try {
      const token = getToken()
      const res   = await fetch('/api/v1/bulk/history', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (res.ok) setHistory(data)
    } catch { /* ignore */ }
    finally { setHistLoading(false) }
  }

  function handleFileChange(e) {
    setFile(e.target.files[0] || null)
    setResult(null)
  }

  async function handleDownloadTemplate() {
    try {
      const token = getToken()
      const res   = await fetch('/api/v1/bulk/template', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('فشل تنزيل القالب')
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = 'martyrform_bulk_template.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(err.message)
    }
  }

  async function handleUpload() {
    if (!file) return
    setUploading(true)
    setResult(null)
    try {
      const token   = getToken()
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch('/api/v1/bulk/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'فشل الاستيراد')
      setResult(data)
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
    } catch (err) {
      setResult({ error: err.message })
    } finally {
      setUploading(false)
    }
  }

  function handleLogout() {
    clearSession()
    setUser(null)
    setResult(null)
    setHistory([])
  }

  if (!user) return <LoginForm onLogin={u => setUser(u)} />

  if (settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-400 text-lg">يتحمّل...</div>
      </div>
    )
  }

  if (!settings.bulk_import_enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4" dir="rtl">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="bg-stone-100 p-5 rounded-full">
              <Ban className="w-10 h-10 text-stone-400" />
            </div>
          </div>
          <h1 className="text-xl font-bold text-stone-800 mb-3">الاستيراد الجماعي معطّل</h1>
          <p className="text-stone-500">ميزة الاستيراد الجماعي معطلة حالياً من قبل الإدارة.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-stone-50 p-4" dir="rtl">
      {/* Header */}
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">الاستيراد الجماعي</h1>
            <p className="text-stone-500 text-sm mt-0.5">مرحباً، {user.display_name || user.username}</p>
          </div>
          <div className="flex gap-2">
            {user.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="text-sm text-amber-700 hover:underline flex items-center gap-1"
              >
                <LayoutDashboard size={14} />
                لوحة الإدارة
              </button>
            )}
            <button
              onClick={handleLogout}
              className="text-sm text-stone-500 hover:text-red-600 flex items-center gap-1"
            >
              <LogOut size={14} />
              خروج
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-200 mb-6">
          {[
            { id: 'upload',  label: 'رفع ملف' },
            { id: 'history', label: 'السجل السابق' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-600 text-amber-700'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Upload tab */}
        {activeTab === 'upload' && (
          <div className="space-y-5">
            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <h2 className="font-semibold text-amber-800 mb-3">خطوات الاستيراد</h2>
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-amber-900">
                <li>قم بتنزيل قالب Excel أدناه</li>
                <li>افتح الملف وأدخل بيانات الشهداء — صف واحد لكل شهيد</li>
                <li>الحقول المميزة بـ (*) إلزامية</li>
                <li>احفظ الملف كـ .xlsx واحذف صف المثال</li>
                <li>ارفع الملف وانتظر نتيجة الاستيراد</li>
              </ol>
            </div>

            {/* Download template */}
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-stone-300 rounded-lg text-sm text-stone-700 hover:bg-stone-50 transition-colors"
            >
              <Download size={15} />
              <span>تنزيل قالب Excel</span>
            </button>

            {/* File picker */}
            <div
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                file
                  ? 'border-amber-400 bg-amber-50'
                  : 'border-stone-300 hover:border-amber-400 bg-white'
              }`}
            >
              {file ? (
                <div>
                  <p className="font-medium text-stone-800">{file.name}</p>
                  <p className="text-sm text-stone-500 mt-1">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div>
                  <FolderOpen size={32} className="mx-auto mb-2 text-stone-400" />
                  <p className="text-stone-600 text-sm">انقر لاختيار ملف Excel</p>
                  <p className="text-stone-400 text-xs mt-1">.xlsx أو .xls</p>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {/* Upload button */}
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full flex items-center justify-center gap-1.5"
            >
              <Upload size={15} />
              {uploading ? 'جارِي الاستيراد...' : 'بدء الاستيراد'}
            </Button>

            {/* Result */}
            {result && (
              <div className={`rounded-xl p-5 border ${result.error ? 'bg-red-50 border-red-200' : 'bg-white border-stone-200'}`}>
                {result.error ? (
                  <p className="text-red-700 font-medium">{result.error}</p>
                ) : (
                  <>
                    <div className="flex gap-6 mb-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{result.success?.length ?? 0}</p>
                        <p className="text-xs text-stone-500">تمت إضافته</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-500">{result.errors?.length ?? 0}</p>
                        <p className="text-xs text-stone-500">فشل</p>
                      </div>
                    </div>

                    {result.success?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-stone-700 mb-2">الحالات المضافة:</p>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {result.success.map(s => (
                            <div key={s.readable_id} className="flex items-center justify-between text-sm bg-green-50 rounded px-3 py-1.5">
                              <span className="text-stone-700">{s.name}</span>
                              <span className="font-mono text-green-700 text-xs">{s.readable_id}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {result.errors?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-stone-700 mb-2">الأخطاء:</p>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {result.errors.map((e, i) => (
                            <div key={i} className="flex items-start justify-between text-sm bg-red-50 rounded px-3 py-1.5">
                              <span className="text-red-700">{e.error}</span>
                              <span className="text-stone-400 text-xs whitespace-nowrap mr-3">صف {e.row}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* History tab */}
        {activeTab === 'history' && (
          <div>
            {histLoading ? (
              <p className="text-stone-500 text-sm">جارٍ التحميل...</p>
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-stone-400">
                <ClipboardList size={36} className="mx-auto mb-2 opacity-40" />
                <p>لا يوجد سجل استيراد سابق</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map(imp => (
                  <div key={imp.id} className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-stone-800 text-sm">{imp.filename}</p>
                      <p className="text-xs text-stone-400">
                        {new Date(imp.created_at).toLocaleString('ar-SY')}
                      </p>
                    </div>
                    <div className="flex gap-4 text-xs text-stone-500">
                      <span>المجموع: <b className="text-stone-700">{imp.total_rows}</b></span>
                      <span className="text-green-600">نجح: <b>{imp.success_rows}</b></span>
                      {imp.failed_rows > 0 && (
                        <span className="text-red-500">فشل: <b>{imp.failed_rows}</b></span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
